import { analyticsConfig } from './config';

declare global {
    interface Window {
        dataLayer: any[];
    }
}

type Gtag = (...args: any[]) => void;

export interface AnalyticsOptions {
    measurementId: string;
    gtag?: Gtag;
    document?: Document;
    storage?: Storage;
    now?: () => number;
}

export interface SearchEvent {
    searchType: string;
    success: boolean;
    resultCount: number;
    hasSearchTerm?: boolean;
    value?: string;
}

export interface RouteLike {
    name?: string | null;
    path: string;
    matched?: Array<{ path: string }>;
}

export interface AnalyticsUserContext {
    analytics_user_id?: string;
    user_type?: string;
    user_role?: string;
}

export interface FormEvent {
    formName: string;
}

export interface FormSubmitEvent extends FormEvent {
    status: string;
}

export interface FileDownloadEvent {
    fileName: string;
    fileType: string;
}

export interface ButtonClickEvent {
    buttonName: string;
}

export interface FilterApplyEvent {
    filterType: string;
    filterCount: number;
}

export interface TabSwitchEvent {
    tabName: string;
}

interface PendingEvent {
    name: string;
    params?: Record<string, any>;
}

const PENDING_EVENTS_KEY = 'tms.analytics.pending_events';
const USER_CONTEXT_KEY = 'tms.analytics.user_context';
const SESSION_STARTED_AT_KEY = 'tms.analytics.session_started_at';
const STAGE_HOSTNAMES = ['clientstage.freightapp.com', 'shipstage.unisco.com'];
const SEARCH_TYPES = ['bol', 'pro', 'po', 'pu', 'shipment_reference'];

function resolveAnalyticsMeasurementId(
    hostname: string,
    measurementId: string,
    uploadStageTraffic: boolean
): string {
    const isStage = STAGE_HOSTNAMES.indexOf(String(hostname || '').toLowerCase()) >= 0;
    return isStage && !uploadStageTraffic ? '' : measurementId;
}

function sanitizePagePath(route: RouteLike): string {
    const matched = route.matched || [];
    const templatePath = matched.length ? matched[matched.length - 1].path : '';
    const path = (templatePath || route.path).split('?')[0].split('#')[0];
    return path
        .split('/')
        .map(segment => isSensitiveSegment(segment) ? ':id' : segment)
        .join('/');
}

function isSensitiveSegment(value: string): boolean {
    return /^\d{5,}$/.test(value) ||
        /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(value) ||
        /^[A-Za-z0-9_-]{24,}$/.test(value);
}

function safeIdentifier(value: string, fallback: string): string {
    const sanitized = String(value || '')
        .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, ' redacted ')
        .replace(/https?:\/\/\S+/gi, ' redacted ')
        .replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ' redacted ')
        .replace(/\d{5,}/g, ' redacted ')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80);
    return sanitized || fallback;
}

function createAnalytics(options: AnalyticsOptions) {
    const measurementId = options.measurementId || '';
    const doc = options.document || (typeof document !== 'undefined' ? document : null);
    const now = options.now || (() => Date.now());
    let storage = options.storage || null;
    if (!storage) {
        try {
            storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null;
        } catch (e) {
            storage = null;
        }
    }
    let gtag = options.gtag;
    let initialized = false;
    let currentPagePath = '/';
    let currentUserType = '';

    const isEnabled = () => Boolean(measurementId && gtag);

    const send = (...args: any[]) => {
        if (isEnabled()) {
            (gtag as Gtag).apply(null, args);
        }
    };

    const readPendingEvents = (): PendingEvent[] => {
        if (!storage) {
            return [];
        }

        try {
            const value = storage.getItem(PENDING_EVENTS_KEY);
            const parsed = value ? JSON.parse(value) : [];
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    };

    const clearPendingEvents = () => {
        if (storage) {
            storage.removeItem(PENDING_EVENTS_KEY);
        }
    };

    const flushPendingEvents = () => {
        const pending = readPendingEvents();
        if (!pending.length || !isEnabled()) {
            return;
        }

        clearPendingEvents();
        pending.forEach(event => send('event', event.name, event.params || {}));
    };

    const queueEvent = (name: string, params: Record<string, any> = {}) => {
        if (name !== 'login' && name !== 'logout') {
            return;
        }

        if (!storage || !measurementId) {
            return;
        }

        let safeParams: Record<string, any>;
        if (name === 'login') {
            const requestedUserType = String(params.user_type || currentUserType).toLowerCase();
            safeParams = {
                method: params.method === 'sso' ? 'sso' : 'password',
                user_type: requestedUserType === 'internal' || requestedUserType === 'external'
                    ? requestedUserType
                    : 'unknown',
            };
            try {
                storage.setItem(SESSION_STARTED_AT_KEY, String(now()));
            } catch (e) {
                // Session timing must never block authentication.
            }
        } else {
            let sessionDuration = 0;
            try {
                const startedAt = Number(storage.getItem(SESSION_STARTED_AT_KEY));
                if (startedAt > 0) {
                    sessionDuration = Math.max(
                        0,
                        Math.min(2592000, Math.floor((now() - startedAt) / 1000))
                    );
                }
                storage.removeItem(SESSION_STARTED_AT_KEY);
            } catch (e) {
                sessionDuration = 0;
            }
            safeParams = params.method === 'forced'
                ? { method: 'forced', session_duration: sessionDuration }
                : { session_duration: sessionDuration };
        }

        // Login/logout flows hard-reload the app; persist before navigation and flush on boot.
        const pending = readPendingEvents();
        pending.push({ name, params: safeParams });
        try {
            storage.setItem(PENDING_EVENTS_KEY, JSON.stringify(pending.slice(-5)));
        } catch (e) {
            // Analytics must never block authentication or navigation.
        }
    };

    const applyUserContext = (context: AnalyticsUserContext | null) => {
        currentUserType = context && context.user_type ? String(context.user_type) : '';
        send('set', { user_id: context && context.analytics_user_id || null });
        send('set', 'user_properties', {
            user_type: context && context.user_type || null,
            user_role: context && context.user_role || null,
        });
    };

    const init = () => {
        if (!measurementId || initialized) {
            flushPendingEvents();
            return;
        }

        if (!gtag && typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            gtag = function() {
                window.dataLayer.push(arguments);
            };

            if (doc) {
                const script = doc.createElement('script');
                script.async = true;
                script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
                    measurementId
                )}`;
                doc.head.appendChild(script);
            }
        }

        if (!gtag) {
            return;
        }

        initialized = true;
        send('js', new Date());
        send('config', measurementId, { send_page_view: false });
        flushPendingEvents();
    };

    return {
        init,
        pageView(route: RouteLike) {
            currentPagePath = sanitizePagePath(route);
            send('event', 'page_view', { page_path: currentPagePath });
        },
        trackFormStart(event: FormEvent) {
            send('event', 'form_start', {
                form_name: safeIdentifier(event.formName, 'form'),
                page_path: currentPagePath,
            });
        },
        trackFormSubmit(event: FormSubmitEvent) {
            const requestedStatus = String(event.status || '').toLowerCase();
            const status = ['attempted', 'success', 'failure'].indexOf(requestedStatus) >= 0
                ? requestedStatus
                : 'attempted';
            send('event', 'form_submit', {
                form_name: safeIdentifier(event.formName, 'form'),
                page_path: currentPagePath,
                status,
            });
        },
        trackFileDownload(event: FileDownloadEvent) {
            send('event', 'file_download', {
                file_name: safeIdentifier(event.fileName, 'download'),
                file_type: safeIdentifier(event.fileType, 'unknown').slice(0, 12),
                page_path: currentPagePath,
            });
        },
        trackButtonClick(event: ButtonClickEvent) {
            send('event', 'button_click', {
                button_name: safeIdentifier(event.buttonName, 'button'),
                page_path: currentPagePath,
            });
        },
        trackFilterApply(event: FilterApplyEvent) {
            const filterCount = Math.max(
                0,
                Math.min(100, Math.floor(Number(event.filterCount) || 0))
            );
            send('event', 'filter_apply', {
                filter_type: safeIdentifier(event.filterType, 'filter'),
                filter_value: filterCount > 0 ? 'applied' : 'cleared',
                filter_count: filterCount,
                page_path: currentPagePath,
            });
        },
        trackTabSwitch(event: TabSwitchEvent) {
            send('event', 'tab_switch', {
                tab_name: safeIdentifier(event.tabName, 'tab'),
                page_path: currentPagePath,
            });
        },
        setUserId(userId: string | null | undefined) {
            send('set', { user_id: userId || null });
        },
        setUserContext(context: AnalyticsUserContext | null | undefined) {
            if (!context) {
                if (storage) {
                    storage.removeItem(USER_CONTEXT_KEY);
                }
                applyUserContext(null);
                return;
            }

            const normalized = {
                analytics_user_id: context.analytics_user_id
                    ? String(context.analytics_user_id)
                    : '',
                user_type: context.user_type ? String(context.user_type) : '',
                user_role: context.user_role ? String(context.user_role) : '',
            };
            if (storage) {
                try {
                    storage.setItem(USER_CONTEXT_KEY, JSON.stringify(normalized));
                } catch (e) {
                    // Analytics must never block authentication or navigation.
                }
            }
            applyUserContext(normalized);
        },
        restoreUserContext() {
            if (!storage) {
                return;
            }

            try {
                const value = storage.getItem(USER_CONTEXT_KEY);
                const context = value ? JSON.parse(value) : null;
                applyUserContext(context && typeof context === 'object' ? context : null);
            } catch (e) {
                storage.removeItem(USER_CONTEXT_KEY);
                applyUserContext(null);
            }
        },
        trackSearch(event: SearchEvent) {
            const requestedSearchType = String(event.searchType || '').toLowerCase();
            const searchType = SEARCH_TYPES.indexOf(requestedSearchType) >= 0
                ? requestedSearchType
                : 'shipment_reference';
            send('event', 'search', {
                search_term: event.hasSearchTerm || event.value ? 'redacted' : 'not_provided',
                search_type: searchType,
                success: Boolean(event.success),
                result_count: Number(event.resultCount) || 0,
                page_path: currentPagePath,
            });
        },
        queueEvent,
        flushPendingEvents,
    };
}

const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const analytics = createAnalytics({
    measurementId: resolveAnalyticsMeasurementId(
        hostname,
        analyticsConfig.measurementId,
        analyticsConfig.uploadStageTraffic
    ),
});

export { analytics, createAnalytics, resolveAnalyticsMeasurementId, sanitizePagePath };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./src/services/analytics/index.ts